import { useLocalStorage } from "@/persist";
import { useEffect } from "react";

type ChangelogState = {
  version: string;
  count: number;
  dontShow: boolean;
  lang: string; // Add language to track which language this state is for
};

export function useChangelogPopup(lang: string, changelogVersion: string, maxCount = 1) {
  const targetVersion = changelogVersion.trim();

  const [state, setState] = useLocalStorage<ChangelogState>(
    "changelog",
    (v: any): v is ChangelogState =>
      typeof v?.version === "string" &&
      typeof v?.count === "number" &&
      typeof v?.dontShow === "boolean" &&
      typeof v?.lang === "string",
    { version: "", count: 0, dontShow: false, lang: "" },
    "persist-by-default",
  );

  // Reset state when language changes OR when configured version changes
  useEffect(() => {
    if (!targetVersion) {
      return;
    }

    // If language changed, reset everything
    if (state.lang !== lang) {
      setState({
        version: targetVersion,
        count: 0,
        dontShow: false,
        lang: lang,
      });
    }
    // If just version changed for same language, update version but keep language
    else if (state.version !== targetVersion) {
      setState({
        ...state,
        version: targetVersion,
        count: 0,
        dontShow: false,
      });
    }
  }, [targetVersion, lang, state.lang, state.version, setState]);

  const shouldShow =
    !!targetVersion &&
    state.version === targetVersion &&
    state.lang === lang &&
    !state.dontShow &&
    state.count < maxCount;

  return {
    loading: false,
    shouldShow,
    version: targetVersion || null,
    count: state.count,
    incrementCount: () => setState({ ...state, count: state.count + 1 }),
    dontShowAgain: () => setState({ ...state, dontShow: true }),
  };
}
